import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../services/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ADS = [
  { id: "promo-cg", image: "/ads/promo-cercle-gourmet.jpg", link: "/catalogue", alt: "Cercle Gourmet — Chocolat en disc — 5 cartons achetés + 1 gratuit" },
  { id: "nocciola", image: "/ads/nocciola.jpg", link: "/catalogue", alt: "Cercle Gourmet — Nocciola 12% Noisette — 10 kg" },
];

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api")) return `${BACKEND}${u}`;
  return u; // local /public asset
};

const AdBanner = () => {
  const [ads, setAds] = useState(DEFAULT_ADS);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let active = true;
    api.getBanners()
      .then((list) => { if (active && Array.isArray(list) && list.length) setAds(list); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const count = ads.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, count]);

  useEffect(() => { if (idx >= count) setIdx(0); }, [count, idx]);

  if (count === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 py-10" data-testid="ad-banner-section">
      <div className="relative overflow-hidden rounded-[2rem] shadow-xl bg-cream">
        <div className="relative aspect-[16/10] sm:aspect-[16/7] md:aspect-[5/2] bg-cream overflow-hidden">
          <div
            className="flex h-full transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${idx * 100}%)` }}
          >
            {ads.map((ad) => (
              <Link
                key={ad.id}
                to={ad.link || "/catalogue"}
                data-testid={`ad-slide-${ad.id}`}
                className="relative shrink-0 w-full h-full"
              >
                {ad.type === "video" ? (
                  <video src={resolveUrl(ad.image)} className="w-full h-full object-contain bg-black" autoPlay muted loop playsInline preload="metadata" />
                ) : (
                  <img src={resolveUrl(ad.image)} alt={ad.alt || "Publicité Ssuq"} className="w-full h-full object-contain" loading="lazy" />
                )}
              </Link>
            ))}
          </div>
        </div>

        {count > 1 && (
          <>
            <button onClick={prev} data-testid="ad-prev-btn" aria-label="Précédent"
              className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-navy flex items-center justify-center shadow-md transition-colors">
              <ChevronLeft size={22} />
            </button>
            <button onClick={next} data-testid="ad-next-btn" aria-label="Suivant"
              className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-navy flex items-center justify-center shadow-md transition-colors">
              <ChevronRight size={22} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {ads.map((ad, i) => (
                <button key={ad.id} onClick={() => setIdx(i)} data-testid={`ad-dot-${i}`} aria-label={`Aller à la publicité ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all ${i === idx ? "w-7 bg-orange" : "w-2.5 bg-white/70 hover:bg-white"}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AdBanner;
